/**
 * character.ts — Pixel character rendering module (Phase 44).
 *
 * Module-level singleton that generates sprites once via getCachedCharacterSprites(),
 * manages 6 animation states (idle, talk, react-hit, react-clear, panic, wave),
 * and renders the character at bottom-left in CSS px coordinates.
 *
 * Caller contract:
 *   - Call initCharacter() once at game start (when isActive mounts).
 *   - Call updateCharacter(dt, typedNewChar) each frame before drawing.
 *   - Call drawCharacter(ctx, cssW, cssH) inside the ctx.save(); ctx.scale(dpr, dpr) block.
 *   - Call setCharacterState(state) at trigger points (hit, clear, panic, wave).
 *
 * Exports: CharacterAnimState, initCharacter, updateCharacter, drawCharacter, setCharacterState
 */

import { getCachedCharacterSprites } from '../office/characterTemplates';
import type { CharacterConfig } from '../office/characterTemplates';
import type { CharacterSprites } from '../office/spriteData';
import { Direction } from '../office/types';
import type { SpriteData } from '../office/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CharacterAnimState = 'idle' | 'talk' | 'react-hit' | 'react-clear' | 'panic' | 'wave';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ANIM_TICK = 8 / 60;         // 8 frames at 60fps = ~133ms per animation frame
const REACT_HIT_DURATION = 0.5;   // seconds for react-hit before auto-returning to idle
const REACT_CLEAR_DURATION = 0.3; // seconds for react-clear before auto-returning to idle
const SCALE = 2;                  // 2x scale: 16x32 sprite -> 32x64 CSS px
const CHAR_X = 8;                 // CSS px from left edge
const CHAR_Y_OFFSET = 72;         // CSS px from bottom edge (y = cssH - CHAR_Y_OFFSET)

const CHARACTER_CONFIG: CharacterConfig = {
  skinTone: 2,
  hairStyle: 'short',
  hairColor: '#4a3728',
  shirtStyle: 'hoodie',
  shirtColor: '#333333',
  pantsColor: '#222222',
  shoeColor: '#111111',
  accessory: null,
};

// ---------------------------------------------------------------------------
// Module-level singleton
// ---------------------------------------------------------------------------

interface CharacterModule {
  sprites: CharacterSprites | null;
  state: CharacterAnimState;
  frameTimer: number;     // accumulates dt (seconds); ticks animation frame every ANIM_TICK
  animFrame: number;      // 0 or 1 for 2-frame states
  reactionTimer: number;  // seconds remaining for react-hit (0.5s) and react-clear (0.3s)
}

const char: CharacterModule = {
  sprites: null,
  state: 'idle',
  frameTimer: 0,
  animFrame: 0,
  reactionTimer: 0,
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * Initialize the character module. Call once at game start (when isActive mounts).
 * Generates sprites via getCachedCharacterSprites (cached after first call).
 * Resets all animation state fields to defaults.
 */
export function initCharacter(): void {
  char.sprites = getCachedCharacterSprites(CHARACTER_CONFIG);
  char.state = 'idle';
  char.frameTimer = 0;
  char.animFrame = 0;
  char.reactionTimer = 0;
}

/**
 * Set character animation state. Resets frame counters.
 * For react-hit and react-clear, starts the reaction timer.
 */
export function getCharacterState(): CharacterAnimState {
  return char.state;
}

export function setCharacterState(state: CharacterAnimState): void {
  char.state = state;
  char.animFrame = 0;
  char.frameTimer = 0;
  if (state === 'react-hit') {
    char.reactionTimer = REACT_HIT_DURATION;
  } else if (state === 'react-clear') {
    char.reactionTimer = REACT_CLEAR_DURATION;
  }
}

/**
 * Update character animation state. Call each frame with delta time in seconds.
 * typedNewChar: true if dialogue typed a new character this frame (drives talk state).
 */
export function updateCharacter(dt: number, typedNewChar: boolean): void {
  if (char.sprites === null) return;

  // Reaction states: countdown timer, auto-return to idle when expired
  if (char.state === 'react-hit' || char.state === 'react-clear') {
    char.reactionTimer -= dt;
    if (char.reactionTimer <= 0) {
      setCharacterState('idle');
    }
    return;
  }

  // talk state: toggle animFrame only when a new dialogue character was typed
  if (char.state === 'talk') {
    if (typedNewChar) {
      char.animFrame = char.animFrame === 0 ? 1 : 0;
    }
    return;
  }

  // idle, panic, wave: toggle animFrame on frameTimer tick
  char.frameTimer += dt;
  if (char.frameTimer >= ANIM_TICK) {
    char.frameTimer -= ANIM_TICK;
    char.animFrame = char.animFrame === 0 ? 1 : 0;
  }
}

/**
 * Draw the character at bottom-left. Must be called inside ctx.save(); ctx.scale(dpr, dpr) block.
 * All coordinates are CSS px.
 */
export function drawCharacter(ctx: CanvasRenderingContext2D, cssW: number, cssH: number): void {
  if (char.sprites === null) return;

  // Suppress unused variable lint for cssW — reserved for future horizontal layout use
  void cssW;

  let frame: SpriteData;
  let drawX: number;
  let drawY: number;

  const baseY = cssH - CHAR_Y_OFFSET;

  switch (char.state) {
    case 'idle':
      frame = char.sprites.walk[Direction.RIGHT][char.animFrame];
      drawX = CHAR_X;
      drawY = baseY + (char.animFrame === 1 ? 1 : 0); // 1px vertical bob
      break;

    case 'talk':
      frame = char.sprites.typing[Direction.RIGHT][char.animFrame];
      drawX = CHAR_X;
      drawY = baseY;
      break;

    case 'react-hit':
      frame = char.sprites.walk[Direction.RIGHT][0];
      drawX = CHAR_X - 2; // 2px recoil left
      drawY = baseY;
      break;

    case 'react-clear':
      frame = char.sprites.walk[Direction.RIGHT][0];
      drawX = CHAR_X;
      drawY = baseY - 2; // 2px bounce up
      break;

    case 'panic':
      frame = char.sprites.walk[Direction.RIGHT][char.animFrame + 2]; // frames 2/3
      drawX = CHAR_X + (char.animFrame === 0 ? -1 : 1); // jitter ±1px
      drawY = baseY;
      break;

    case 'wave':
      frame = char.sprites.reading[Direction.RIGHT][char.animFrame];
      drawX = CHAR_X;
      drawY = baseY;
      break;
  }

  drawSprite(ctx, frame, drawX, drawY);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function drawSprite(ctx: CanvasRenderingContext2D, sprite: SpriteData, x: number, y: number): void {
  for (let row = 0; row < sprite.length; row++) {
    for (let col = 0; col < sprite[row].length; col++) {
      const color = sprite[row][col];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x + col * SCALE, y + row * SCALE, SCALE, SCALE);
    }
  }
}
